'use client';
import {
	ComponentStatus,
	useAmountInput,
	useAutoFocusAfterEnablingInput,
	useIntl,
	type WalletLimits
} from '@capsicum/common';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { type FieldError } from 'react-hook-form';
import styled, { type DefaultTheme } from 'styled-components';
import { match } from 'ts-pattern';

import { useRTL } from '../../../../hooks/helpers/useRTL';
import { TextLineOrSkeleton } from '../../../atoms';
import { ErrorWithTooltip } from '../../../atoms/tooltip/ErrorWithTooltip';
import { InfoTooltip } from '../../../atoms/tooltip/InfoTooltip';

export type BaseAmountInputProps = {
	/**
	 * Optional, show a skeleton instead of conversionPrice
	 */
	areConversionRatesLoading?: boolean;

	/**
	 * Optional, the input converted value
	 */
	conversionPrice?: string;
	/**
	 * Optional amount of decimal points.
	 */
	decimalDetail?: number;
	/**
	 * Optional, Boolean to to disabled the component
	 */
	disabled?: boolean;
	/**
	 * Optional error.
	 */
	error?: FieldError & {
		button?: { label: string; onClick?: () => void };
		tooltip?: string;
		validationType?: WalletLimits;
		/**
		 * if true, the error will not show a message, but will be highlighted as an error.
		 */
		withoutMessage?: boolean;
	};
	/**
	 * Optional show an error tooltip description.
	 */
	errorTooltipContent?: string;
	/**
	 * Optional, the max value of input.
	 * Accepts a number or a string (e.g., from .toFixed() or .toString()) to support high-precision decimals.
	 */
	max?: number | string;
	/**
	 * The minimal value of input.
	 * Accepts a number or a string (e.g., from .toFixed() or .toString()) to support high-precision decimals.
	 */
	min?: number | string;
	/**
	 * Callback fired when the input value changes.
	 */
	onChange?: (value: string) => void;
	/**
	 * Optional handle color but not show error message
	 */
	showErrorMessage?: boolean;
	/**
	 * TestID of the input element
	 */
	testID: string;
	/**
	 * Optional, Message to display in the tooltip
	 */
	tooltipContent?: JSX.Element | string;
	/**
	 * Optional, The value to display in the input
	 */
	value?: string;
};

export type AmountInputInputRef = {
	blur: () => void;
	focus: () => void;
};

export const BaseAmountInput = forwardRef<AmountInputInputRef, BaseAmountInputProps>(function BaseAmountInput(
	{
		areConversionRatesLoading,
		conversionPrice = '',
		decimalDetail = 2,
		disabled = false,
		error,
		errorTooltipContent,
		max,
		min,
		onChange,
		showErrorMessage = true,
		testID = 'amountInput',
		tooltipContent,
		value = '0'
	},
	forwardedRef
) {
	const { isRTL } = useRTL();
	const { $t } = useIntl();

	const inputRef = useRef<HTMLInputElement>(null);

	const { inputValue, isFocus, onAmountInputChange, onBlur, onFocus } = useAmountInput(value, inputRef);

	const minMaxCheck = {
		isMax: max !== undefined,
		isMin: min !== undefined,
		isMinMax: min !== undefined || max !== undefined
	};

	const inputStatus = useMemo(() => {
		if (error?.message || error?.withoutMessage) return ComponentStatus.ERROR;

		if (disabled) return ComponentStatus.DISABLED;

		if (isFocus) return ComponentStatus.FOCUSED;

		return ComponentStatus.BLUR;
	}, [error, disabled, isFocus]);

	useAutoFocusAfterEnablingInput(inputRef, true, disabled);

	useImperativeHandle(forwardedRef, () => ({
		blur: () => {
			inputRef.current?.blur();
		},
		focus: () => {
			inputRef.current?.focus();
		}
	}));

	return (
		<S.AmountWrapper>
			<S.Input
				$isRTL={isRTL}
				$status={inputStatus}
				autoFocus
				data-testid={testID + '-input'}
				disabled={disabled}
				inputMode="decimal"
				ref={inputRef}
				step={decimalDetail ? 0.01 : 1}
				type="text"
				value={inputValue}
				onBlur={onBlur}
				onChange={e => onAmountInputChange({ decimalDetail, onChange, value: e.target.value })}
				onFocus={onFocus}
			/>
			{minMaxCheck.isMinMax && (
				<S.MinMaxWrapper>
					<S.FullWidthDiv />
					{tooltipContent && (
						<InfoTooltip contentPosition="bottom_left" testID={testID} zIndex={15}>
							{tooltipContent}
						</InfoTooltip>
					)}
					<S.MinMaxText>
						<TextLineOrSkeleton isLoading={areConversionRatesLoading} lineWidth={120}>
							{minMaxCheck.isMin && (
								<S.MinMax data-testid={`${testID}-minLimit`}>
									{$t('payments.amountInput.min', { min: min })}
								</S.MinMax>
							)}
							{minMaxCheck.isMax && (
								<S.MinMax data-testid={`${testID}-maxLimit`}>
									{`${minMaxCheck.isMin ? ' - ' : ''}${$t('payments.amountInput.max', { max: max })}`}
								</S.MinMax>
							)}
						</TextLineOrSkeleton>
					</S.MinMaxText>
				</S.MinMaxWrapper>
			)}

			{conversionPrice && (
				<S.ConversionText>
					<TextLineOrSkeleton isLoading={areConversionRatesLoading} lineWidth={80}>
						{conversionPrice}
					</TextLineOrSkeleton>
				</S.ConversionText>
			)}
			{error?.message && showErrorMessage && (
				<ErrorWithTooltip errorMessage={error.message} errorTooltipContent={errorTooltipContent} />
			)}
		</S.AmountWrapper>
	);
});

const getColorStatus = (theme: DefaultTheme, status: ComponentStatus) =>
	match(status)
		.with(ComponentStatus.DISABLED, () => theme.colors.text.main.disabled)
		.with(ComponentStatus.ERROR, () => theme.colors.borders.error.enabled)
		.with(ComponentStatus.FOCUSED, () => theme.colors.text.brand.enabled)
		.with(ComponentStatus.BLUR, () => theme.colors.text.brand.unselected)
		.exhaustive();

namespace S {
	export const AmountWrapper = styled.div`
		text-align: right;
		flex: 1;
	`;

	export const Input = styled.input<{ $isRTL: boolean; $status: ComponentStatus; disabled?: boolean }>`
		outline: none;
		border: none;
		transition: 0.1s ease-out;
		width: 100%;
		background: unset;
		padding: 0;
		${({ theme }) => theme.fonts.headings.title1};
		text-align: ${({ $isRTL }) => ($isRTL ? 'left' : 'right')};
		color: ${({ $status, theme }) => getColorStatus(theme, $status)};
		opacity: 1; /* WebKit lowers opacity for disabled inputs */
		appearance: textfield; /* WebKit removes the inner spin button */

		/*
			WebKit had a bug that was fixed with Safari v17 in September 2023,
			where disabled input fields would get the wrong text color. Setting
			-webkit-text-fill-color to the same value as color is a workaround.
			This can eventually be removed.
		*/
		-webkit-text-fill-color: ${({ $status, theme }) => getColorStatus(theme, $status)};

		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}
	`;

	export const MinMaxWrapper = styled.div`
		display: flex;
		align-items: center;
		justify-content: end;
	`;

	export const MinMaxText = styled.div`
		display: flex;
		align-items: center;
		justify-content: end;
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const MinMax = styled.div`
		${({ theme }) => theme.fonts.text.text5};

		&:first-child {
			margin-left: ${({ theme }) => theme.spacings.xxxsmall};
		}
	`;
	export const ErrorContainer = styled.span`
		display: flex;
		align-items: center;
		justify-content: end;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const ErrorLabel = styled.p`
		color: ${({ theme }) => theme.colors.borders.error.enabled};
		${({ theme }) => theme.fonts.text.text5};
	`;
	export const ErrorTooltipContent = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0;
	`;
	export const ConversionText = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	//Needed to resolve issue with a content alignment on a Nexus 5 (Android webView)
	export const FullWidthDiv = styled.div`
		display: flex;
		flex-grow: 1;
	`;
}
